import React, { useState, useEffect, useCallback } from 'react';
import { Search, UserCheck, UserX, Eye, Edit2, Coffee, Loader2, X, CalendarClock, RefreshCw, Bike, Save, BarChart3, MapPin } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import MemberStatisticsModal from './MemberStatisticsModal';
import MemberRegionsModal from './MemberRegionsModal';
import MemberShiftsModal from './MemberShiftsModal';
import './MembersPage.css';

const VEHICLE_TYPES = [
  { value: 'Motorcycle', label: 'Motosiklet' },
  { value: 'Bicycle', label: 'Bisiklet' },
  { value: 'Car', label: 'Otomobil' },
  { value: 'Scooter', label: 'Scooter' },
  { value: 'OnFoot', label: 'Yaya' },
];

// margin YOK: grid satırlarının aralığı .form-group'un row-gap'inden gelir (bkz. index.css subgrid notu).
const fieldHintStyle = { margin: 0, fontSize: '12px', lineHeight: 1.4, color: 'var(--text-secondary)' };

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [shiftModal, setShiftModal] = useState(null);
  const [statsModal, setStatsModal] = useState(null);
  const [regionsModal, setRegionsModal] = useState(null);

  const loadMembers = useCallback(async () => {
    try {
      setLoading(true);
      const body = { page, pageSize };
      if (searchTerm) {
        body.filterModel = { CourierName: { filter: searchTerm, type: 'contains' } };
      }
      body.sortModels = [{ colId: 'JoinedAt', sort: 'desc' }];
      const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.MEMBERS_LIST, body);
      if (response.success) {
        setMembers(response.data?.items || []);
        const total = response.data?.totalCount || 0;
        setTotalCount(total);
        setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
      }
    } catch (error) {
      console.error('Failed to load members:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  useEffect(() => {
    const handler = () => loadMembers();
    window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handler);
    return () => window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handler);
  }, [loadMembers]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); };

  const handleToggleStatus = async (memberId, isActive) => {
    try {
      setActionLoading(`status-${memberId}`);
      const endpoint = isActive ? API_ENDPOINTS.FLEET_PANEL.MEMBER_TERMINATE(memberId) : API_ENDPOINTS.FLEET_PANEL.MEMBER_ACTIVATE(memberId);
      const res = await apiService.put(endpoint);
      if (res.success) { fleetToast.success(isActive ? 'Üyelik sonlandırıldı' : 'Üyelik aktifleştirildi'); await loadMembers(); }
      else { fleetToast.error(res.error || 'İşlem başarısız'); }
    } catch { fleetToast.error('İşlem sırasında hata oluştu'); } finally { setActionLoading(null); }
  };

  const handleToggleBusy = async (memberId, currentBusy) => {
    try {
      setActionLoading(`busy-${memberId}`);
      const res = await apiService.patch(API_ENDPOINTS.FLEET_PANEL.MEMBER_BUSY_STATUS(memberId), { isBusy: !currentBusy });
      if (res.success) { fleetToast.success(!currentBusy ? 'Kurye meşgul olarak işaretlendi' : 'Kurye müsait olarak işaretlendi'); await loadMembers(); }
      else { fleetToast.error(res.error || 'İşlem başarısız'); }
    } catch { fleetToast.error('İşlem sırasında hata oluştu'); } finally { setActionLoading(null); }
  };

  const handleViewDetail = async (memberId) => {
    try {
      const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.MEMBER_DETAIL(memberId));
      if (response.success) setDetailModal(response.data);
    } catch { fleetToast.error('Detay yüklenemedi'); }
  };

  const handleOpenEdit = (member) => {
    setEditForm({
      fullName: member.courierName || '',
      phone: member.courierPhone || '',
      vehicleType: member.vehicleType || 'Motorcycle',
      vehiclePlate: member.vehiclePlate || '',
      paymentType: member.paymentType ?? 0
    });
    setEditModal(member);
  };

  const handleEditSave = async () => {
    if (!editModal) return;
    try {
      setEditSaving(true);
      const res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.MEMBER_UPDATE_COURIER(editModal.id), editForm);
      if (res.success) { fleetToast.success('Kurye bilgileri güncellendi'); setEditModal(null); await loadMembers(); }
      else { fleetToast.error(res.error || 'Güncelleme başarısız'); }
    } catch { fleetToast.error('Güncelleme sırasında hata oluştu'); } finally { setEditSaving(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getVehicleLabel = (type) => VEHICLE_TYPES.find(v => v.value === type)?.label || type || '-';

  return (
    <div className="fm-container">
      <div className="page-header">
        <div className="header-left">
          <div className="page-icon"><UserCheck size={20} /></div>
          <div className="page-title">
            <h1>Kuryelerim</h1>
            <p>Filonuza bağlı kuryeleri yönetin <span className="count-badge">{totalCount}</span></p>
          </div>
        </div>
        <div className="header-right">
          <button className="btn btn-secondary" onClick={loadMembers} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} /> Yenile
          </button>
        </div>
      </div>

      <div className="page-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <Search size={16} />
            <input type="text" placeholder="Kurye adı ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
          </div>
          <button type="submit" className="btn btn-primary">Ara</button>
        </form>
      </div>

      {loading ? (
        <div className="page-loading"><Loader2 className="spinning" size={40} /><p>Yükleniyor...</p></div>
      ) : members.length === 0 ? (
        <div className="page-empty"><UserCheck size={48} /><h3>Henüz üye yok</h3><p>Filonuza katılan kuryeler burada görünecek.</p></div>
      ) : (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kurye Adı</th>
                  <th>Telefon</th>
                  <th>Araç</th>
                  <th>Plaka</th>
                  <th>Durum</th>
                  <th>Meşguliyet</th>
                  <th>Katılma Tarihi</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td className="cell-name">
                      {m.courierName || '-'}
                      {/* Bölgesiz kurye atama havuzuna girmez → sipariş alamaz. */}
                      {m.isActive && m.hasRegionAssignment === false && (
                        <button
                          type="button"
                          className="fm-no-region-badge"
                          title="Bu kuryenin bölge ataması yok, sipariş alamaz. Bölge atamak için tıklayın."
                          onClick={() => setRegionsModal(m)}
                        >
                          <MapPin size={12} /> Bölgesiz
                        </button>
                      )}
                      {m.paymentType === 1 && (
                        <span className="fm-payroll-badge" title="Bordro ile ödenir, fatura beklenmez">
                          Bordro
                        </span>
                      )}
                    </td>
                    <td>{m.courierPhone || '-'}</td>
                    <td><span className="fm-vehicle-badge"><Bike size={14} />{getVehicleLabel(m.vehicleType)}</span></td>
                    <td><span className="cell-mono">{m.vehiclePlate || '-'}</span></td>
                    <td><span className={`status-badge ${m.isActive ? 'active' : 'inactive'}`}>{m.isActive ? 'Aktif' : 'Pasif'}</span></td>
                    <td>
                      <button className={`fm-busy-toggle ${m.isBusy ? 'busy' : 'available'}`} onClick={() => handleToggleBusy(m.id, m.isBusy)} disabled={actionLoading === `busy-${m.id}` || !m.isActive}>
                        {actionLoading === `busy-${m.id}` ? <Loader2 size={14} className="spinning" /> : m.isBusy ? <><Coffee size={14} /> Meşgul</> : <><UserCheck size={14} /> Müsait</>}
                      </button>
                    </td>
                    <td>{formatDate(m.joinedAt)}</td>
                    <td>
                      <div className="fm-actions">
                        <button className="action-btn edit" title="Düzenle" onClick={() => handleOpenEdit(m)}><Edit2 size={16} /></button>
                        <button className="action-btn info" title="İstatistik" onClick={() => setStatsModal(m)}><BarChart3 size={16} /></button>
                        <button className="action-btn purple" title="Vardiyalar" onClick={() => setShiftModal(m)}><CalendarClock size={16} /></button>
                        <button className="action-btn view" title="Bölgeler" onClick={() => setRegionsModal(m)}><MapPin size={16} /></button>
                        <button className="action-btn view" title="Detay" onClick={() => handleViewDetail(m.id)}><Eye size={16} /></button>
                        <button className={`action-btn ${m.isActive ? 'delete' : 'success'}`} title={m.isActive ? 'Sonlandır' : 'Aktifleştir'} onClick={() => handleToggleStatus(m.id, m.isActive)} disabled={actionLoading === `status-${m.id}`}>
                          {actionLoading === `status-${m.id}` ? <Loader2 size={16} className="spinning" /> : m.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="simple-pagination">
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Önceki</button>
            <span>Sayfa {page} / {totalPages}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Sonraki</button>
          </div>
        </>
      )}

      {detailModal && (
        <div className="modal-overlay" onClick={() => setDetailModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Üye Detayı</h2>
              <button className="modal-close" onClick={() => setDetailModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item"><span className="detail-label">Kurye Adı</span><span className="detail-value">{detailModal.courierName || '-'}</span></div>
                <div className="detail-item"><span className="detail-label">Telefon</span><span className="detail-value">{detailModal.courierPhone || '-'}</span></div>
                <div className="detail-item"><span className="detail-label">Plaka</span><span className="detail-value">{detailModal.vehiclePlate || '-'}</span></div>
                <div className="detail-item"><span className="detail-label">Araç Tipi</span><span className="detail-value">{getVehicleLabel(detailModal.vehicleType)}</span></div>
                <div className="detail-item"><span className="detail-label">Üyelik Türü</span><span className="detail-value">{detailModal.membershipType || '-'}</span></div>
                <div className="detail-item"><span className="detail-label">Katılma Tarihi</span><span className="detail-value">{formatDate(detailModal.joinedAt)}</span></div>
                <div className="detail-item"><span className="detail-label">Ayrılma Tarihi</span><span className="detail-value">{formatDate(detailModal.leftAt)}</span></div>
                <div className="detail-item"><span className="detail-label">Durum</span><span className={`status-badge ${detailModal.isActive ? 'active' : 'inactive'}`}>{detailModal.isActive ? 'Aktif' : 'Pasif'}</span></div>
                <div className="detail-item"><span className="detail-label">Onay</span><span className="detail-value">{detailModal.isApproved ? 'Onaylı' : 'Onay Bekliyor'}</span></div>
                <div className="detail-item"><span className="detail-label">Meşguliyet</span><span className={`status-badge ${detailModal.isBusy ? 'warning' : 'active'}`}>{detailModal.isBusy ? 'Meşgul' : 'Müsait'}</span></div>
                {detailModal.notes && (<div className="detail-item detail-full"><span className="detail-label">Notlar</span><span className="detail-value">{detailModal.notes}</span></div>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Kurye Düzenle</h2>
              <button className="modal-close" onClick={() => setEditModal(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group"><label>Ad Soyad *</label><input type="text" value={editForm.fullName} onChange={(e) => setEditForm(f => ({ ...f, fullName: e.target.value }))} placeholder="Ad Soyad" /></div>
                <div className="form-group"><label>Telefon</label><input type="tel" value={editForm.phone} onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))} placeholder="+90..." /></div>
                <div className="form-group"><label>Araç Tipi *</label><select value={editForm.vehicleType} onChange={(e) => setEditForm(f => ({ ...f, vehicleType: e.target.value }))}>{VEHICLE_TYPES.map(v => (<option key={v.value} value={v.value}>{v.label}</option>))}</select></div>
                <div className="form-group"><label>Plaka *</label><input type="text" value={editForm.vehiclePlate} onChange={(e) => setEditForm(f => ({ ...f, vehiclePlate: e.target.value }))} placeholder="34ABC123" /></div>
                <div className="form-group">
                  <label>Çalışma biçimi</label>
                  <select
                    value={editForm.paymentType}
                    onChange={(e) => setEditForm(f => ({ ...f, paymentType: Number(e.target.value) }))}
                  >
                    <option value={0}>Serbest — filoya fatura keser</option>
                    <option value={1}>SGK'lı çalışan — bordro, fatura kesmez</option>
                  </select>
                  <p style={fieldHintStyle}>
                    SGK'lı seçilirse kuryenin hakedişi fatura beklemez, doğrudan onaya düşer.
                  </p>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setEditModal(null)} disabled={editSaving}>İptal</button>
                <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving || !editForm.fullName || !editForm.vehiclePlate}>
                  {editSaving ? <Loader2 size={16} className="spinning" /> : <Save size={16} />}
                  {editSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {shiftModal && <MemberShiftsModal member={shiftModal} onClose={() => setShiftModal(null)} />}
      {statsModal && <MemberStatisticsModal member={statsModal} onClose={() => setStatsModal(null)} />}
      {regionsModal && <MemberRegionsModal member={regionsModal} onClose={() => setRegionsModal(null)} />}
    </div>
  );
};

export default MembersPage;
